import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Store, Users, ShoppingBag, Euro } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <AdminLayout title="Tableau de bord">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-2xl animate-pulse" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Tableau de bord">
      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Restaurants</CardTitle>
            <Store className="w-5 h-5 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.restaurants?.total || 0}</div>
            <p className="text-xs text-gray-500 mt-1">
              {stats?.restaurants?.active || 0} actifs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Utilisateurs</CardTitle>
            <Users className="w-5 h-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.users?.total || 0}</div>
            <p className="text-xs text-gray-500 mt-1">
              {stats?.users?.clients || 0} clients
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Commandes du jour</CardTitle>
            <ShoppingBag className="w-5 h-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.orders?.today || 0}</div>
            <p className="text-xs text-gray-500 mt-1">
              {stats?.orders?.month || 0} ce mois
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Revenu du mois</CardTitle>
            <Euro className="w-5 h-5 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{(stats?.revenue?.month || 0).toFixed(2)} €</div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-900 mb-4">Actions rapides</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <a 
            href="/admin/restaurants" 
            className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <Store className="w-8 h-8 text-emerald-500 mb-2" />
            <h3 className="font-medium text-gray-900">Gérer les restaurants</h3>
            <p className="text-sm text-gray-500">Activer, désactiver, voir</p>
          </a>
          <a 
            href="/admin/users" 
            className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <Users className="w-8 h-8 text-blue-500 mb-2" />
            <h3 className="font-medium text-gray-900">Gérer les utilisateurs</h3>
            <p className="text-sm text-gray-500">Clients et restaurants</p>
          </a>
          <a 
            href="/admin/orders" 
            className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <ShoppingBag className="w-8 h-8 text-orange-500 mb-2" />
            <h3 className="font-medium text-gray-900">Voir les commandes</h3>
            <p className="text-sm text-gray-500">Toutes les commandes</p>
          </a>
        </div>
      </div>
    </AdminLayout>
  );
}
